import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./AuthContext.js";
import { authApi } from "../api/auctiongate.js";
import { clearToken, getToken, setToken } from "../api/client.js";

const USER_KEY = "ag_auth_user";

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user) {
  try {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [token, setTokenState] = useState(() => getToken());
  const [bootstrapping, setBootstrapping] = useState(() => !!getToken());

  useEffect(() => {
    if (!token) {
      setBootstrapping(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await authApi.validate();
        if (cancelled) return;
        if (!r?.valid) {
          clearToken();
          writeStoredUser(null);
          setTokenState(null);
          setUser(null);
        }
      } catch {
        clearToken();
        writeStoredUser(null);
        setTokenState(null);
        setUser(null);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = useCallback(async ({ login, password, rememberMe }) => {
    const r = await authApi.login({ login, password, rememberMe });
    if (!r?.success || !r?.token) {
      const err = new Error(r?.error || r?.response || "login_failed");
      err.payload = r;
      throw err;
    }
    setToken(r.token);
    writeStoredUser(r.user);
    setTokenState(r.token);
    setUser(r.user);
    return r;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {}
    clearToken();
    writeStoredUser(null);
    setTokenState(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, token, isAuthenticated: !!token && !!user, bootstrapping, login, logout }),
    [user, token, bootstrapping, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
